import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowUDownLeft, Plus, MicrosoftExcelLogo, MagnifyingGlass, Warning, ArrowSquareOut } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SavCaseDialog } from "../components/sav/SavCaseDialog";
import { SavSuppliers } from "../components/sav/SavSuppliers";
import { STATUSES, OUTCOMES, StatusBadge, errMsg, fmtDay, daysSince, downloadBlob, trackingUrl } from "../components/sav/savShared";

function CaseRow({ c, staleDays, onOpen }) {
  const stale = c.status !== "done" && daysSince(c.status_changed_at) > staleDays;
  const t = trackingUrl(c.return_carrier, c.tracking_number);
  return (
    <tr className="border-b last:border-0 hover:bg-muted/50 cursor-pointer" onClick={() => onOpen(c.id)} data-testid={`sav-row-${c.id}`}>
      <td className="px-3 py-2 font-mono text-xs">{c.number}</td>
      <td className="px-3 py-2"><div className="font-medium">{c.product_name}</div><div className="text-[11px] text-muted-foreground">{[c.product_ref, c.serial_number && `S/N ${c.serial_number}`].filter(Boolean).join(" · ")}</div></td>
      <td className="px-3 py-2 text-sm">{c.supplier_name || "—"}</td>
      <td className="px-3 py-2 font-mono text-xs">{c.rma_number || "—"}</td>
      <td className="px-3 py-2 text-xs">
        {c.tracking_number ? (t ? <a href={t} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 hover:underline">{c.tracking_number}<ArrowSquareOut size={11} /></a> : c.tracking_number) : "—"}
      </td>
      <td className="px-3 py-2"><StatusBadge status={c.status} />{stale && <span className="ml-1 inline-flex items-center gap-0.5 text-[11px] text-red-600" title="Retour qui traîne" data-testid={`sav-stale-${c.id}`}><Warning size={11} weight="fill" />{daysSince(c.status_changed_at)} j</span>}</td>
      <td className="px-3 py-2 text-xs">{c.outcome ? OUTCOMES[c.outcome] : "—"}{c.outcome === "refunded" && c.refund_amount != null ? ` · ${c.refund_amount.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}` : ""}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground">{fmtDay(c.created_at)}</td>
    </tr>
  );
}

function CaseList({ refreshKey, suppliers, staleDays, onOpen }) {
  const [status, setStatus] = useState("waiting_return,in_progress");
  const [q, setQ] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], pages: 1, total: 0, counts: {} });
  const params = { q: q || undefined, status: status || undefined, supplier_id: supplierId || undefined };
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/sav/cases", { params: { ...params, page, per_page: 25 } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [q, status, supplierId, page, refreshKey]); // eslint-disable-line
  useEffect(() => { setPage(1); }, [q, status, supplierId]);
  const filters = [["waiting_return,in_progress", "Ouverts"], ["waiting_return", STATUSES.waiting_return.label], ["in_progress", STATUSES.in_progress.label], ["done", STATUSES.done.label], ["", "Tous"]];
  const exportXlsx = async () => { try { await downloadBlob(`/sav/export?${new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString()}`, `sav_${new Date().toISOString().slice(0, 10)}.xlsx`); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid="sav-case-list">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {filters.map(([id, label]) => {
          const n = id ? id.split(",").reduce((s, k) => s + (data.counts?.[k] || 0), 0) : null;
          return <Button key={id || "all"} size="sm" variant={status === id ? "default" : "ghost"} onClick={() => setStatus(id)} data-testid={`sav-filter-${id || "all"}`}>{label}{n !== null ? ` (${n})` : ""}</Button>;
        })}
      </div>
      <div className="mb-3 flex flex-wrap gap-2">
        <div className="relative min-w-[220px] flex-1"><MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-8" placeholder="Produit, n° de série, RMA, suivi, fournisseur…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="sav-search" /></div>
        <select className="rounded-md border bg-background px-3 py-2 text-sm" value={supplierId} onChange={(e) => setSupplierId(e.target.value)} data-testid="sav-filter-supplier">
          <option value="">Tous les fournisseurs</option>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <Button variant="outline" onClick={exportXlsx} data-testid="sav-export"><MicrosoftExcelLogo size={16} className="mr-1" />Export Excel</Button>
      </div>
      <div className="overflow-x-auto rounded-xl border bg-card">
        <table className="w-full min-w-[860px] text-sm">
          <thead><tr className="border-b bg-muted/60 text-left text-xs text-muted-foreground">
            {["Dossier", "Produit", "Fournisseur", "RMA", "Suivi retour", "Statut", "Issue", "Créé le"].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>{data.items.map((c) => <CaseRow key={c.id} c={c} staleDays={staleDays} onOpen={onOpen} />)}</tbody>
        </table>
        {!data.items.length && <div className="p-10 text-center text-sm text-muted-foreground" data-testid="sav-empty">Aucun dossier ici.</div>}
      </div>
      <div className="mt-3 flex items-center justify-between text-sm">
        <span className="text-muted-foreground" data-testid="sav-total">{data.total} dossier(s)</span>
        {data.pages > 1 && <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="sav-prev-page">Précédent</Button>
          <span className="font-mono" data-testid="sav-page-indicator">Page {page} / {data.pages}</span>
          <Button variant="secondary" size="sm" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="sav-next-page">Suivant</Button>
        </div>}
      </div>
    </div>
  );
}

function AlertSettings({ settings, onSaved }) {
  const [s, setS] = useState(settings);
  useEffect(() => { setS(settings); }, [settings]);
  const save = async () => { try { onSaved((await api.put("/sav/settings", { alert_enabled: s.alert_enabled, alert_days: Number(s.alert_days) })).data); toast.success("Réglages enregistrés"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="max-w-lg rounded-xl border bg-card p-4 space-y-3" data-testid="sav-settings">
      <div className="font-semibold">Alerte des retours qui traînent</div>
      <label className="flex items-center gap-2 text-sm"><Switch checked={s.alert_enabled} onCheckedChange={(v) => setS({ ...s, alert_enabled: v })} data-testid="sav-alert-enabled" />Envoyer un email aux admins</label>
      <label className="flex items-center gap-2 text-sm">quand un dossier ouvert n'a pas changé de statut depuis
        <Input type="number" min={1} max={365} className="w-20" value={s.alert_days} onChange={(e) => setS({ ...s, alert_days: e.target.value })} data-testid="sav-alert-days" />jours</label>
      <p className="text-xs text-muted-foreground">Vérification chaque matin à 8h. Un même dossier est rappelé au plus une fois par semaine.{settings.last_alert_at ? ` Dernière alerte : ${new Date(settings.last_alert_at).toLocaleString("fr-FR")}.` : ""}</p>
      <Button size="sm" onClick={save} data-testid="sav-settings-save">Enregistrer</Button>
    </div>
  );
}

export default function Sav() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("cases");
  const [open, setOpen] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [settings, setSettings] = useState({ alert_enabled: true, alert_days: 14 });
  const [refreshKey, setRefreshKey] = useState(0);
  const loadSuppliers = () => api.get("/sav/suppliers").then((r) => setSuppliers(r.data.items))
    .catch((e) => { toast.error(errMsg(e)); if (e?.response?.status === 403) nav("/modules"); });
  useEffect(() => { loadSuppliers(); api.get("/sav/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []); // eslint-disable-line
  const bump = () => setRefreshKey((k) => k + 1);
  return (
    <div className="p-4 sm:p-6 max-w-7xl">
      <button type="button" onClick={() => nav("/modules")} className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="sav-back"><ArrowLeft size={14} /> Modules</button>
      <header className="mb-6 flex flex-wrap items-end gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 text-2xl sm:text-3xl font-display font-bold" data-testid="sav-title"><ArrowUDownLeft size={28} className="shrink-0" /> SAV<span className="hidden sm:inline"> — Retours produits</span></h1>
          <p className="mt-1 text-sm text-muted-foreground">Retours fournisseurs, RMA, suivi des colis, échanges et remboursements.</p>
        </div>
        <Button onClick={() => setOpen({ id: null })} data-testid="sav-new-case"><Plus size={16} className="mr-1" />Nouveau dossier</Button>
      </header>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 max-w-full justify-start overflow-x-auto">
          <TabsTrigger value="cases" data-testid="sav-tab-cases">Dossiers</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="sav-tab-suppliers">Fournisseurs</TabsTrigger>
          {isAdmin && <TabsTrigger value="settings" data-testid="sav-tab-settings">Réglages</TabsTrigger>}
        </TabsList>
        <TabsContent value="cases"><CaseList refreshKey={refreshKey} suppliers={suppliers} staleDays={settings.alert_days} onOpen={(id) => setOpen({ id })} /></TabsContent>
        <TabsContent value="suppliers"><SavSuppliers isAdmin={isAdmin} refreshKey={refreshKey} onChanged={loadSuppliers} /></TabsContent>
        {isAdmin && <TabsContent value="settings"><AlertSettings settings={settings} onSaved={setSettings} /></TabsContent>}
      </Tabs>
      <SavCaseDialog open={!!open} caseId={open?.id} onClose={() => setOpen(null)} onSaved={bump} suppliers={suppliers} reloadSuppliers={loadSuppliers} />
    </div>
  );
}
